<template>
  <div
    ref="target"
    v-loading.fullscreen="loading"
    class="game-version-selector-component"
    :class="{ expanded: isExpanded }"
  >
    <section class="information-wrapper" @click="toggle">
      <div class="main-information">
        <el-image class="game-icon" :src="game.iconUrl" fit="cover" />
        <div class="main-name">{{ game.name }}</div>
      </div>
      <div v-if="currentVersion" class="selected-item">
        <vd-custom-icon
          v-if="currentVersion.icon"
          :color="currentVersion.isWip ? '#067bf0' : null"
          :name="currentVersion.icon"
        />
        <span class="version-name">
          <span class="version-label">{{ currentVersion.isLive ? "Live Version" : currentVersion.name }}</span>
          <span v-if="currentVersion.isLive" class="version-live-name">{{ currentVersion.name }}</span>
        </span>
        <VdIcon store="fa" :name="isExpanded ? 'chevron-up' : 'chevron-down'" size="small" />
      </div>
    </section>
    <section v-if="currentVersion" class="dropdown">
      <div @click="onVersionSelected(wipVersion.humanId)">
        <GameVersion v-if="wipVersion" :selected="currentVersion.isWip" :version="wipVersion" />
      </div>
      <div @click="onVersionSelected(liveVersion.humanId)">
        <GameVersion v-if="liveVersion" :selected="currentVersion.isLive" :version="liveVersion" />
      </div>
      <section class="dropdown-category">
        <span>Previous versions</span>
        <span>{{ previousVersions.length }}</span>
      </section>
      <div v-for="version in previousVersions" :key="version.id" @click="onVersionSelected(version.humanId)">
        <GameVersion :selected="version.id === currentVersion.id" :version="version" />
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { onClickOutside } from "@vueuse/core";
import { defineComponent, computed, ref, PropType } from "vue";
import { useRouter } from "vue-router";

import { icons } from "@/constants";
import { GameDisplayPageGameFragment, GameDisplayPageVersionFragment } from "@/graphql";
import useToggle from "@/hooks/toggle";
import links from "@/router/links";

import GameVersion from "./GameVersion.vue";

export default defineComponent({
  name: "GameVersionSelector",

  components: {
    // TrafficPlotIcon,
    // LiveIcon,
    GameVersion,
    VdIcon,
  },

  props: {
    game: {
      type: Object as PropType<GameDisplayPageGameFragment>,
      default: () => ({
        iconUrl: "",
        name: "Game Name",
      }),
    },

    currentVersionId: {
      type: String,
      required: true,
    },

    versions: {
      type: Array as PropType<GameDisplayPageVersionFragment[]>,
      required: true,
    },
  },

  setup(props) {
    const router = useRouter();
    const { state: isExpanded, toggle } = useToggle(false);

    /**
     * If the menu is expanded, toggle it when clicking outside
     */
    const target = ref(null);
    onClickOutside(target, () => {
      if (isExpanded.value) {
        toggle();
      }
    });

    /**
     * Getting the current/selected version
     */
    const currentVersion = computed(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      () => props.versions.find((v) => props.currentVersionId === v.humanId)!
    );

    /**
     * Getting back versions by statuses
     */
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const getVersionByStatus = (status: string) => props.versions.find((v) => v.status === status && v !== undefined)!;

    const wipVersion = computed(() => getVersionByStatus("wip"));

    const liveVersion = computed(() => getVersionByStatus("live"));

    /**
     * Filter the version which status is history, those will be used for previous versions section
     */
    const previousVersions = computed(() => props.versions.filter((v) => v.status === "history" && v !== undefined));

    /**
     * Handles a version selection
     */
    const loading = ref(false);
    const onVersionSelected = async (humanVersionId: string) => {
      toggle();
      if (humanVersionId !== props.currentVersionId) {
        router.push({
          name: links.games.home,
          params: { gameId: props.game.id, versionId: humanVersionId },
        });
      }
    };

    return {
      target,
      icons,
      currentVersion,
      previousVersions,
      wipVersion,
      liveVersion,
      onVersionSelected,
      toggle,
      isExpanded,
      loading,
    };
  },
});
</script>

<style lang="scss" scoped>
.game-version-selector-component {
  display: flex;
  position: relative;
  justify-content: space-between;
  align-items: center;
  width: 400px;
  height: 24px;
  box-sizing: content-box;
  padding: size("S");
  padding-right: 8px;
  margin-right: size("M");
  cursor: pointer;
  @extend %card;

  background-color: var(--vd-input-background-color);
  border-color: var(--vd-input-border-color);

  @include medium-screen {
    width: 240px;
  }

  .information-wrapper {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    width: 100%;

    @include medium-screen {
      grid-template-columns: size("L") 1fr;
    }

    .main-information {
      justify-self: left;
      display: grid;
      grid-template-columns: auto 1fr;
      align-items: center;
      grid-gap: 7px;

      .game-icon {
        width: 26px;
        height: 26px;
      }

      .main-name {
        font-size: 15px;

        @include medium-screen {
          display: none;
        }
      }
    }

    .selected-item {
      justify-self: right;
      display: grid;
      grid-template-columns: repeat(3, auto);
      align-items: center;
      cursor: pointer;

      font-size: font("S");
      font-weight: 600;

      .version-name,
      i {
        color: $intense-blue;
      }

      .version-name {
        padding-left: size("XS");
        max-width: 150px;

        .version-label {
          display: block;
          @include ellipsis;
        }

        .version-live-name {
          display: block;
          font-size: 10px;
          font-weight: 400;
          @include ellipsis;
        }
      }

      .vd-icon {
        margin: 0 3px 0 7px;
        color: $intense-blue;
        // margin-top: 3px; // Align perfectly the arrow next to the text
      }
    }
  }

  .dropdown {
    @extend %card;
    visibility: hidden;
    opacity: 0;
    transition: visibility 0s linear $animation-timing, opacity $animation-timing ease-out;

    position: absolute;
    z-index: $z-game-version-selector;
    top: 100%;
    width: 100%;
    margin-left: -9px;
    box-sizing: content-box;

    max-height: 250px; // Four and half versions (including category bar), so the user know he can scroll
    overflow-y: scroll;

    box-shadow: 0 02px 12px 0 rgba(0, 0, 0, 0.1);

    .dropdown-category {
      display: grid;
      grid-template-columns: 1fr 25px;
      width: 100%;

      :nth-child(1) {
        padding: 2px size("M");
      }

      :nth-child(2) {
        padding: 2px 0;
      }

      color: $grey;
    }
  }

  &.expanded {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;

    .dropdown {
      visibility: visible;
      opacity: 1;
      transition: visibility 0s linear 0s, opacity $animation-timing ease-in;

      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }
  }
}
</style>
